import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icon";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import {
  useGetOnboardingStatusQuery,
  useOptOutOnboardingMutation,
  useSeedOnboardingMutation,
} from "~/redux/api/onboarding";

import { COUNTRIES } from "~/lib/countries";
import VeilighLogo from "~/assets/logos/veiligh_new_logo.png";

const INDUSTRIES = [
  "Mining & Metals",
  "Construction & Engineering",
  "Oil & Gas",
  "Manufacturing",
  "Healthcare & Pharma",
  "Logistics & Transport",
  "Aviation",
  "Agriculture & Forestry",
  "Power & Utilities",
  "Technology & Telecom",
  "Services & Retail",
];

const SelectionSummary = ({
  industries,
  countries,
}: {
  industries?: string[];
  countries?: string[];
}) => {
  if (
    (!industries || industries.length === 0) &&
    (!countries || countries.length === 0)
  )
    return null;

  return (
    <div className="flex flex-col gap-2 bg-neutral-950/40 border border-neutral-800/60 rounded-xl p-3 text-xs mb-2">
      {industries && industries.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-neutral-500 font-medium mr-1 font-Outfit">
            Selected Industries:
          </span>
          {industries.map((ind) => (
            <span
              key={ind}
              className="bg-neutral-800 text-neutral-300 px-2 py-0.5 rounded-full border border-neutral-700 font-Outfit text-[10px] font-medium"
            >
              {ind}
            </span>
          ))}
        </div>
      )}
      {countries && countries.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-neutral-500 font-medium mr-1 font-Outfit">
            Selected Countries:
          </span>
          {countries.map((c) => (
            <span
              key={c}
              className="bg-neutral-800 text-neutral-300 px-2 py-0.5 rounded-full border border-neutral-700 font-Outfit text-[10px] font-medium"
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function OnboardingModal() {
  const { data: statusData, refetch: refetchStatus } =
    useGetOnboardingStatusQuery();
  const [optOut] = useOptOutOutMutationCustom();
  const [seedOnboarding, { isLoading: isSeeding }] =
    useSeedOnboardingMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [skipSeeding, setSkipSeeding] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  // Seeding logs simulation
  const [seedingLogs, setSeedingLogs] = useState<string[]>([]);

  // Helper hook for opt out since it's destructured
  function useOptOutOutMutationCustom() {
    return useOptOutOnboardingMutation();
  }

  useEffect(() => {
    if (statusData?.status === "Pending") {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [statusData]);

  const handleOptOut = async () => {
    try {
      await optOut().unwrap();
      toast.info("Onboarding wizard skipped.");
      setIsOpen(false);
      refetchStatus();
    } catch (error) {
      toast.error("Failed to opt out of onboarding.");
    }
  };

  const handleNextToCountries = () => {
    if (selectedIndustries.length === 0) {
      toast.error("Please select at least one industry sector.");
      return;
    }
    setStep(3);
  };

  const handleConfirmSeeding = async () => {
    setStep(5);
    setSeedingLogs(["Initializing onboarding seeder..."]);

    // Simulate step progress logs
    const logSteps = [
      { text: "Establishing secure connection with OpenAI...", delay: 1500 },
      {
        text: "Personalizing incident and observation categories...",
        delay: 3500,
      },
      { text: "Setting up regional governing bodies...", delay: 6000 },
      {
        text: "Seeding cause, consequence, and illness tables...",
        delay: 8500,
      },
      { text: "Configuring compliance management templates...", delay: 11000 },
      { text: "Finalizing data integrity check...", delay: 13500 },
    ];

    logSteps.forEach((stepLog) => {
      setTimeout(() => {
        setSeedingLogs((prev) => [...prev, stepLog.text]);
      }, stepLog.delay);
    });

    try {
      await seedOnboarding({
        industry: selectedIndustries.join(", "),
        countries: selectedCountries,
        selectedRegulations: [],
        selectedRegulators: [],
        selectedStandards: [],
      }).unwrap();

      setSeedingLogs((prev) => [...prev, "✓ Seeding completed successfully!"]);
      toast.success("HSE Workspace successfully configured!");

      setTimeout(() => {
        setIsOpen(false);
        refetchStatus();
        window.location.reload();
      }, 1500);
    } catch (error) {
      setSeedingLogs((prev) => [
        ...prev,
        "✗ Configuration failed. Please retry.",
      ]);
      toast.error("Failed to complete onboarding seeding.");
    }
  };

  const handleNextToConfirmation = () => {
    if (selectedCountries.length === 0) {
      toast.error("Please select at least one operating country.");
      return;
    }
    handleConfirmSeeding();
  };

  const toggleCountry = (country: string) => {
    if (selectedCountries.includes(country)) {
      setSelectedCountries(selectedCountries.filter((c) => c !== country));
    } else {
      setSelectedCountries([...selectedCountries, country]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[640px] bg-neutral-900 border-neutral-800 text-white p-8 rounded-2xl shadow-2xl overflow-hidden font-Outfit"
        noClose={true}
      >
        {step === 1 && (
          <div className="space-y-6">
            <DialogHeader className="text-center space-y-4 flex flex-col items-center justify-center">
              <span className="inline-block h-auto w-36 text-center">
                <img src={VeilighLogo} alt="Veiligh Logo" className="mx-auto" />
              </span>
              <DialogTitle className="text-2xl font-bold text-white tracking-wide text-center w-full">
                Welcome to Veiligh
              </DialogTitle>
              <DialogDescription className="text-neutral-400 text-base leading-relaxed text-center max-w-md mx-auto">
                Personalize your workspace. We can seed standard safety
                regulations, regulatory bodies, and industry-specific metrics
                tailored to your business profile.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-xl border border-neutral-800 bg-neutral-950/40 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Icon
                  name="Check"
                  className="h-5 w-5 text-primary-500 mt-0.5"
                />
                <span className="text-sm text-neutral-300">
                  Seeds standard incident types, cause classifications, and
                  effect metrics.
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Icon
                  name="Check"
                  className="h-5 w-5 text-primary-500 mt-0.5"
                />
                <span className="text-sm text-neutral-300">
                  Pre-configures local compliance frameworks and regulatory
                  bodies.
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Icon
                  name="Check"
                  className="h-5 w-5 text-primary-500 mt-0.5"
                />
                <span className="text-sm text-neutral-300">
                  Saves manual configuration time so you can begin tracking
                  immediately.
                </span>
              </div>
            </div>

            <div
              className="flex items-center gap-3 border border-neutral-800 bg-neutral-950/40 p-4 rounded-xl cursor-pointer"
              onClick={() => setSkipSeeding(!skipSeeding)}
            >
              <Checkbox
                id="skip-seeding"
                checked={skipSeeding}
                onCheckedChange={(checked) => setSkipSeeding(!!checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <label
                htmlFor="skip-seeding"
                className="text-sm text-neutral-300 cursor-pointer select-none"
              >
                Start with a blank workspace (do not seed safety categories,
                regulations, or standards)
              </label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1 border border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-800 h-11"
              >
                Decide Later
              </Button>
              <Button
                variant="secondary"
                onClick={skipSeeding ? handleOptOut : () => setStep(2)}
                className="flex-1 font-medium h-11"
              >
                {skipSeeding
                  ? "Start with Blank Workspace"
                  : "Configure Workspace"}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <DialogHeader className="text-center sm:text-left">
              <DialogTitle className="text-xl font-bold text-white">
                Industry Profile
              </DialogTitle>
              <DialogDescription className="text-neutral-400 text-sm">
                Select the primary industry sectors that apply to your business.
              </DialogDescription>
            </DialogHeader>

            <SelectionSummary industries={selectedIndustries} />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-neutral-300">
                  Available Sectors
                </label>
                {selectedIndustries.length > 0 && (
                  <span className="text-xs text-secondary-500 font-medium">
                    {selectedIndustries.length} selected
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 max-h-[220px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-neutral-800 scrollbar-track-transparent">
                {INDUSTRIES.map((industry) => {
                  const isChecked = selectedIndustries.includes(industry);
                  return (
                    <div
                      key={industry}
                      onClick={() => {
                        if (isChecked) {
                          setSelectedIndustries(
                            selectedIndustries.filter((i) => i !== industry),
                          );
                        } else {
                          setSelectedIndustries([
                            ...selectedIndustries,
                            industry,
                          ]);
                        }
                      }}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                        isChecked
                          ? "border-secondary-500 bg-secondary-500/5 text-white"
                          : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-700"
                      }`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => {}}
                      />
                      <span className="text-xs font-medium">{industry}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-800 h-11"
              >
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={handleNextToCountries}
                className="flex-1 font-medium h-11 flex items-center justify-center gap-2"
              >
                <span>Next</span>
                <Icon name="ArrowRight" className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <DialogHeader className="text-center sm:text-left">
              <DialogTitle className="text-xl font-bold text-white">
                Operating Countries
              </DialogTitle>
              <DialogDescription className="text-neutral-400 text-sm">
                Select your countries of operation.
              </DialogDescription>
            </DialogHeader>

            <SelectionSummary
              industries={selectedIndustries}
              countries={selectedCountries}
            />

            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-neutral-300">
                    Operating Countries
                  </label>
                  {selectedCountries.length > 0 && (
                    <span className="text-xs text-secondary-500 font-medium">
                      {selectedCountries.length} selected
                    </span>
                  )}
                </div>

                <div className="relative">
                  <Icon
                    name="Search"
                    className="absolute left-3 top-2.5 h-4 w-4 text-neutral-500"
                  />
                  <Input
                    type="text"
                    placeholder="Search operating countries..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    className="w-full h-9 rounded-xl border border-neutral-800 bg-neutral-950 text-white pl-9 pr-4 text-xs placeholder:text-neutral-500 focus-visible:ring-1 focus-visible:ring-secondary-500 focus-visible:ring-offset-0"
                  />
                </div>

                <div className="grid grid-cols-3 gap-2 max-h-[220px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-neutral-800 scrollbar-track-transparent">
                  {COUNTRIES.filter((c) =>
                    c.label.toLowerCase().includes(countrySearch.toLowerCase()),
                  ).map((country) => {
                    const isChecked = selectedCountries.includes(country.value);
                    return (
                      <div
                        key={country.value}
                        onClick={() => toggleCountry(country.value)}
                        className={`flex items-center gap-2 rounded-xl border p-2 cursor-pointer transition-all ${
                          isChecked
                            ? "border-secondary-500 bg-secondary-500/5 text-white"
                            : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-700"
                        }`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => {}}
                        />
                        <span
                          className="text-[11px] font-medium leading-none truncate"
                          title={country.label}
                        >
                          {country.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 border border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-800 h-11"
              >
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={handleNextToConfirmation}
                disabled={isSeeding}
                className="flex-1 font-medium h-11 flex items-center justify-center gap-2"
              >
                {isSeeding ? (
                  <span>Preparing Workspace...</span>
                ) : (
                  <>
                    <span>Next</span>
                    <Icon name="ArrowRight" className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6">
            <DialogHeader className="text-center sm:text-left">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-xl font-bold text-white">
                  Personalizing Workspace
                </DialogTitle>
                <Icon
                  name="Loader"
                  className="h-5 w-5 animate-spin text-primary-500"
                />
              </div>
              <DialogDescription className="text-neutral-400 text-sm">
                Generating option tables and compliance registry records
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-xl border border-neutral-800 bg-neutral-950 p-5 font-mono text-xs text-neutral-400 space-y-2 min-h-[140px] max-h-[180px] overflow-y-auto">
              {seedingLogs.map((log, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-neutral-600">❯</span>
                  <span
                    className={
                      log.startsWith("✓")
                        ? "text-primary-500"
                        : log.startsWith("✗")
                          ? "text-destructive"
                          : "text-neutral-300"
                    }
                  >
                    {log}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
